import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_USER, DELETE_USER, LIST_USERS, UPDATE_USER_ACCESS } from '../graphql/operations';
import authService from '../auth/authService';
import { PATIENT_WORKFLOW_PERMISSION_OPTIONS, ORDERS_WORKFLOW_PERMISSION_OPTIONS } from '../permissions/workflowPermissions';
import './AdminShared.css';
import './UserManagement.css';

const ROLE_OPTIONS = [
  { value: 'system_admin', label: 'System Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'employee', label: 'Employee' }
];

const QUICK_ROLE_CHIPS = [
  { value: '', label: 'All' },
  { value: 'system_admin', label: 'System Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'employee', label: 'Employee' }
];

const EMPLOYEE_PERMISSION_OPTIONS = [
  ...PATIENT_WORKFLOW_PERMISSION_OPTIONS,
  ...ORDERS_WORKFLOW_PERMISSION_OPTIONS,
];

const DEFAULT_EMPLOYEE_PERMISSIONS = EMPLOYEE_PERMISSION_OPTIONS.map((option) => option.value);

const PERMISSION_LABEL_BY_VALUE = EMPLOYEE_PERMISSION_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const PANEL_PERMISSION_GROUPS = [
  {
    key: 'patient',
    title: 'Patient workflows',
    description: 'Control who can retrieve, add, and update governed patient records.',
    options: PATIENT_WORKFLOW_PERMISSION_OPTIONS
  },
  {
    key: 'orders',
    title: 'Order workflows',
    description: 'Control who can retrieve, create, update, and archive governed orders.',
    options: ORDERS_WORKFLOW_PERMISSION_OPTIONS
  }
];

function createDefaultPanelForm() {
  return {
    email: '',
    displayName: '',
    role: 'employee',
    permissions: [...DEFAULT_EMPLOYEE_PERMISSIONS]
  };
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

function rolePillClass(role) {
  if (role === 'system_admin') return 'admin-pill-purple';
  if (role === 'admin') return 'admin-pill-blue';
  return 'admin-pill-gray';
}

function roleLabel(role) {
  return role.replace(/_/g, ' ');
}

function permissionPillText(permission) {
  return PERMISSION_LABEL_BY_VALUE[permission] || permission;
}

function getPrimaryRole(user) {
  return user.roles?.[0] || 'employee';
}

function UserManagement() {
  const currentUsername = authService.getUsername();
  const isSystemAdmin = authService.hasRole('system_admin');
  const isAdmin = authService.hasRole('admin');

  const [statusMessage, setStatusMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [panelMode, setPanelMode] = useState(null);
  const [panelUser, setPanelUser] = useState(null);
  const [panelForm, setPanelForm] = useState(createDefaultPanelForm);

  const panelTitleId = 'user-management-panel-title';
  const panelDescriptionId = 'user-management-panel-description';
  const panelEmailInputRef = useRef(null);
  const panelRoleSelectRef = useRef(null);
  const panelTriggerRef = useRef(null);

  const { data, loading, error, refetch } = useQuery(LIST_USERS, {
    fetchPolicy: 'network-only'
  });

  const closePanel = useCallback(() => {
    setPanelMode(null);
    setPanelUser(null);
    setPanelForm(createDefaultPanelForm());
    window.requestAnimationFrame(() => {
      panelTriggerRef.current?.focus?.();
    });
  }, []);

  const [updateUserAccess, { loading: savingAccess }] = useMutation(UPDATE_USER_ACCESS, {
    onCompleted: () => {
      setStatusMessage({ type: 'success', text: 'User access updated.' });
      closePanel();
      refetch();
    },
    onError: (err) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to update user.' });
    }
  });

  const [deleteUser, { loading: deletingUser }] = useMutation(DELETE_USER, {
    onCompleted: () => {
      setStatusMessage({ type: 'success', text: 'User deleted.' });
      refetch();
    },
    onError: (err) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to delete user.' });
    }
  });

  const [createUser, { loading: creatingUser }] = useMutation(CREATE_USER, {
    onCompleted: () => {
      setStatusMessage({ type: 'success', text: 'User created. Display name will sync from Google on first sign-in.' });
      closePanel();
      refetch();
    },
    onError: (err) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to create user.' });
    }
  });

  const users = useMemo(() => data?.listUsers || [], [data]);

  const filteredUsers = useMemo(() => users.filter((user) => {
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !search ||
      (user.displayName || '').toLowerCase().includes(search) ||
      (user.email || '').toLowerCase().includes(search) ||
      (user.username || '').toLowerCase().includes(search);

    const matchesRole = !roleFilter || (user.roles || []).includes(roleFilter);
    return matchesSearch && matchesRole;
  }), [users, searchTerm, roleFilter]);

  const activeFilterCount = useMemo(
    () => [searchTerm, roleFilter].filter((value) => Boolean(value && String(value).trim())).length,
    [searchTerm, roleFilter]
  );

  const roleCounts = useMemo(() => users.reduce((acc, user) => {
    const role = getPrimaryRole(user);
    acc[role] = (acc[role] || 0) + 1;
    return acc;
  }, { system_admin: 0, admin: 0, employee: 0 }), [users]);

  const isPanelOpen = Boolean(panelMode);
  const isPanelEmployeeRole = panelForm.role === 'employee';
  const panelBusy = creatingUser || savingAccess || deletingUser;

  useEffect(() => {
    if (!isPanelOpen) return undefined;

    const timer = window.setTimeout(() => {
      if (panelMode === 'create') {
        panelEmailInputRef.current?.focus();
      } else {
        panelRoleSelectRef.current?.focus();
      }
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isPanelOpen, panelMode]);

  useEffect(() => {
    if (!isPanelOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closePanel();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isPanelOpen, closePanel]);

  const canEditUser = (user) => {
    if (isSystemAdmin) return true;
    if (!isAdmin) return false;
    return !(user.roles || []).includes('system_admin');
  };

  const canDeleteUser = (user) => {
    if (user.username === currentUsername) return false;
    if (isSystemAdmin) return true;
    if (!isAdmin) return false;
    return !(user.roles || []).includes('system_admin');
  };

  const openCreatePanel = (event) => {
    panelTriggerRef.current = event.currentTarget;
    setPanelMode('create');
    setPanelUser(null);
    setPanelForm(createDefaultPanelForm());
  };

  const openEditPanel = (user, event) => {
    panelTriggerRef.current = event.currentTarget;
    const role = getPrimaryRole(user);

    setPanelMode('edit');
    setPanelUser(user);
    setPanelForm({
      email: user.email || user.username || '',
      displayName: user.displayName || '',
      role,
      permissions: role === 'employee'
        ? Array.isArray(user.permissions) ? [...user.permissions] : []
        : ['*']
    });
  };

  const handlePanelRoleChange = (role) => {
    setPanelForm((prev) => ({
      ...prev,
      role,
      permissions: role === 'employee' ? [...DEFAULT_EMPLOYEE_PERMISSIONS] : ['*']
    }));
  };

  const handlePanelPermissionToggle = (permission, checked) => {
    setPanelForm((prev) => {
      const next = new Set(prev.permissions || []);
      if (checked) {
        next.add(permission);
      } else {
        next.delete(permission);
      }
      return {
        ...prev,
        permissions: [...next]
      };
    });
  };

  const handlePanelSelectAllPermissions = () => {
    setPanelForm((prev) => ({
      ...prev,
      permissions: [...DEFAULT_EMPLOYEE_PERMISSIONS]
    }));
  };

  const handlePanelClearPermissions = () => {
    setPanelForm((prev) => ({
      ...prev,
      permissions: []
    }));
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Remove ${user.displayName || user.email}? This cannot be undone.`)) return;
    await deleteUser({ variables: { username: user.username } });
  };

  const handlePanelSubmit = async (event) => {
    event.preventDefault();

    if (panelMode === 'create') {
      if (!panelForm.email.trim()) {
        setStatusMessage({ type: 'error', text: 'Email is required.' });
        return;
      }

      await createUser({
        variables: {
          input: {
            email: panelForm.email.trim().toLowerCase(),
            displayName: panelForm.displayName.trim() || undefined,
            roles: [panelForm.role],
            permissions: panelForm.role === 'employee' ? panelForm.permissions : ['*']
          }
        }
      });
      return;
    }

    if (!panelUser) return;

    await updateUserAccess({
      variables: {
        input: {
          username: panelUser.username,
          roles: [panelForm.role],
          permissions: panelForm.role === 'employee' ? panelForm.permissions : ['*'],
          displayName: panelUser.displayName || panelUser.username
        }
      }
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
  };

  const renderPermissionsReadMode = (selectedRole, permissions) => {
    if (selectedRole !== 'employee' || permissions.includes('*')) {
      return <span className="admin-pill admin-pill-sm admin-pill-green">Full access</span>;
    }

    if (permissions.length === 0) {
      return <span className="admin-pill admin-pill-sm admin-pill-gray">No permissions</span>;
    }

    if (permissions.length <= 3) {
      return permissions.map((permission) => (
        <span key={permission} className="admin-pill admin-pill-sm admin-pill-gray">
          {permissionPillText(permission)}
        </span>
      ));
    }

    return (
      <>
        {permissions.slice(0, 2).map((permission) => (
          <span key={permission} className="admin-pill admin-pill-sm admin-pill-gray">
            {permissionPillText(permission)}
          </span>
        ))}
        <span className="admin-pill admin-pill-sm admin-pill-gray">+{permissions.length - 2}</span>
      </>
    );
  };

  if (loading) return <div className="um-state">Loading users...</div>;
  if (error) return <div className="um-state um-error">Error: {error.message}</div>;

  return (
    <section className="um">
      <header className="admin-view-header">
        <div>
          <h2>User Management</h2>
          <p>Manage which governed patient workflows each employee can view and request.</p>
        </div>
        {isSystemAdmin && (
          <button
            type="button"
            className="um-add-btn"
            onClick={openCreatePanel}
            disabled={panelBusy}
          >
            + Add User
          </button>
        )}
      </header>

      {statusMessage && (
        <div
          className={`um-toast ${statusMessage.type === 'error' ? 'um-toast-error' : 'um-toast-success'}`}
          onClick={() => setStatusMessage(null)}
          role="status"
        >
          {statusMessage.text}
        </div>
      )}

      <div className="admin-filters um-filters">
        <div className="um-filter-toolbar">
          <div className="um-filter-summary">
            <span className="admin-pill admin-pill-sm admin-pill-gray">{activeFilterCount} active filters</span>
            <span className="um-filter-summary-text">Quick role chips keep permission audits fast.</span>
          </div>
          <div className="um-filter-actions">
            <div className="um-role-chips" role="group" aria-label="Quick role filters">
              {QUICK_ROLE_CHIPS.map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  className={`um-role-chip ${roleFilter === chip.value ? 'is-active' : ''}`}
                  onClick={() => setRoleFilter(chip.value)}
                  aria-pressed={roleFilter === chip.value}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <button type="button" className="um-clear-btn" onClick={handleClearFilters}>Clear filters</button>
          </div>
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Search</span>
          <input
            className="admin-filter-input"
            type="text"
            placeholder="Name or email"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="admin-filter-field">
          <span className="admin-filter-label">Role</span>
          <select
            className="admin-filter-input"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
          >
            <option value="">All roles</option>
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <section className="um-summary-row" aria-label="User summary">
        <article className="um-summary-card">
          <span>Total users</span>
          <strong>{users.length}</strong>
        </article>
        <article className="um-summary-card">
          <span>System admins</span>
          <strong>{roleCounts.system_admin || 0}</strong>
        </article>
        <article className="um-summary-card">
          <span>Admins</span>
          <strong>{roleCounts.admin || 0}</strong>
        </article>
        <article className="um-summary-card">
          <span>Employees</span>
          <strong>{roleCounts.employee || 0}</strong>
        </article>
      </section>

      <div className="admin-results-count">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      <div className="admin-card-list um-user-list">
        {filteredUsers.length === 0 && (
          <div className="um-empty">No users match the current filters.</div>
        )}

        {filteredUsers.map((user) => {
          const selectedRole = getPrimaryRole(user);
          const permissions = Array.isArray(user.permissions) ? user.permissions : [];
          const canEdit = canEditUser(user);
          const canDelete = canDeleteUser(user);
          const initials = getInitials(user.displayName || user.username);

          return (
            <div className="admin-card-row um-card" key={user.username}>
              <div className="um-card-left">
                <div
                  className="admin-avatar um-avatar"
                  data-provider={user.authProvider || 'unknown'}
                  style={{ width: 40, height: 40, background: 'var(--primary)', fontSize: 14 }}
                >
                  {initials}
                </div>
                <div className="um-card-info">
                  <div className="um-card-name">{user.displayName || user.username}</div>
                  <div className="um-card-email">{user.email || user.username}</div>
                </div>
              </div>

              <div className="um-card-controls">
                <div className="um-card-role">
                  <span className={`admin-pill admin-pill-sm ${rolePillClass(selectedRole)}`}>
                    {roleLabel(selectedRole)}
                  </span>
                </div>

                <div className="um-card-perms">
                  {renderPermissionsReadMode(selectedRole, permissions)}
                </div>

                <div className="um-card-provider">
                  <span className={`um-provider-badge ${user.authProvider === 'google' ? 'um-google' : ''}`}>
                    {user.authProvider === 'google' ? 'Google' : (user.authProvider || 'password')}
                  </span>
                </div>
              </div>

              <div className="um-card-actions">
                {canEdit && (
                  <button
                    type="button"
                    className="um-action-btn um-save"
                    onClick={(event) => openEditPanel(user, event)}
                    disabled={panelBusy}
                    title="Edit user"
                  >
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="um-action-btn um-delete"
                    onClick={() => handleDelete(user)}
                    disabled={panelBusy}
                    title="Delete user"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isPanelOpen && (
        <div
          className="um-panel-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closePanel();
            }
          }}
        >
          <aside
            className="um-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby={panelTitleId}
            aria-describedby={panelDescriptionId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="um-panel-header">
              <div>
                <h3 id={panelTitleId}>{panelMode === 'create' ? 'Add User' : 'Edit User Access'}</h3>
                <p id={panelDescriptionId}>
                  {panelMode === 'create'
                    ? 'Add a governed user and define role-based workflow permissions.'
                    : 'Adjust role and workflow permissions without changing backend RBAC contracts.'}
                </p>
              </div>
              <button
                type="button"
                className="um-panel-close"
                onClick={closePanel}
                disabled={panelBusy}
              >
                Close
              </button>
            </div>

            <form className="um-panel-form" onSubmit={handlePanelSubmit}>
              <div className="um-panel-body">
                <section className="um-panel-section">
                  <div className="um-panel-section-head">
                    <h4>Identity</h4>
                  </div>

                  {panelMode === 'create' ? (
                    <div className="um-panel-fields">
                      <label className="um-field">
                        <span>Google Email</span>
                        <input
                          ref={panelEmailInputRef}
                          type="email"
                          placeholder="name@kapsa.ai"
                          value={panelForm.email}
                          onChange={(event) => setPanelForm((prev) => ({ ...prev, email: event.target.value }))}
                          required
                        />
                      </label>

                      <label className="um-field">
                        <span>Display Name (Optional)</span>
                        <input
                          type="text"
                          placeholder="Auto-syncs on first Google sign-in"
                          value={panelForm.displayName}
                          onChange={(event) => setPanelForm((prev) => ({ ...prev, displayName: event.target.value }))}
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="um-panel-identity-card">
                      <div className="um-card-name">{panelUser?.displayName || panelUser?.username}</div>
                      <div className="um-card-email">{panelUser?.email || panelUser?.username}</div>
                      <div className="um-panel-identity-row">
                        <span className={`um-provider-badge ${panelUser?.authProvider === 'google' ? 'um-google' : ''}`}>
                          {panelUser?.authProvider === 'google' ? 'Google' : (panelUser?.authProvider || 'password')}
                        </span>
                      </div>
                    </div>
                  )}
                </section>

                <section className="um-panel-section">
                  <div className="um-panel-section-head">
                    <h4>Role</h4>
                  </div>
                  <label className="um-field">
                    <span>Access Role</span>
                    <select
                      ref={panelRoleSelectRef}
                      value={panelForm.role}
                      onChange={(event) => handlePanelRoleChange(event.target.value)}
                      disabled={panelBusy}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={!isSystemAdmin && option.value === 'system_admin'}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </section>

                <section className="um-panel-section">
                  <div className="um-panel-section-head um-panel-section-head-split">
                    <h4>Permissions</h4>
                    {isPanelEmployeeRole && (
                      <div className="um-perm-actions">
                        <button
                          type="button"
                          className="um-perm-action-btn"
                          onClick={handlePanelSelectAllPermissions}
                          disabled={panelBusy}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="um-perm-action-btn"
                          onClick={handlePanelClearPermissions}
                          disabled={panelBusy}
                        >
                          Clear all
                        </button>
                      </div>
                    )}
                  </div>

                  {isPanelEmployeeRole ? (
                    <div className="um-permission-groups" role="group" aria-label="Employee workflow permissions">
                      {PANEL_PERMISSION_GROUPS.map((group) => (
                        <fieldset key={group.key} className="um-permission-group">
                          <legend>{group.title}</legend>
                          <p>{group.description}</p>
                          <div className="um-permission-grid">
                            {group.options.map((option) => (
                              <label key={option.value} className="um-perm-check">
                                <input
                                  type="checkbox"
                                  checked={(panelForm.permissions || []).includes(option.value)}
                                  onChange={(event) => handlePanelPermissionToggle(option.value, event.target.checked)}
                                  disabled={panelBusy}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                        </fieldset>
                      ))}
                    </div>
                  ) : (
                    <div className="um-full-access-note">
                      <span className="admin-pill admin-pill-sm admin-pill-green">Full access</span>
                      <p>Admins and system admins retain wildcard permissions (`*`).</p>
                    </div>
                  )}
                </section>

                <div className="um-panel-hint">
                  Name and avatar sync automatically from Google on first sign-in.
                </div>
              </div>

              <div className="um-panel-footer">
                <button
                  type="button"
                  className="um-action-btn um-cancel"
                  onClick={closePanel}
                  disabled={panelBusy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="um-submit-btn"
                  disabled={panelBusy || (panelMode === 'create' && !panelForm.email.trim())}
                >
                  {panelMode === 'create'
                    ? (creatingUser ? 'Creating...' : 'Create User')
                    : (savingAccess ? 'Saving...' : 'Save Changes')}
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
    </section>
  );
}

export default UserManagement;
